import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Card,
  DatePicker,
  Button,
  Table,
  Typography,
  message,
  Select,
  Space,
  Tooltip,
} from "antd";
import { SwapOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { getProfitAndLoss } from "../../api/accounting";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import { getCachedData, setCachedData, CacheTTL } from "../../context/AccountingDataCache";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ProfitAndLoss = () => {
  // Enable quarter calculations (startOf('quarter') / endOf('quarter'))
  dayjs.extend(quarterOfYear);
  const { selectedBrands, selectedClients } = useGlobalFilters();
  
  // Currency conversion
  const { displayCurrency, exchangeRate, isConverted } = useDisplayCurrency();

  const [dateRange, setDateRange] = useState([]);
  const [dateFilter, setDateFilter] = useState("this_month");
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Normalize brandIds directly from selectedBrands - no fallback
  const normalizedBrandIds = useMemo(() => {
    if (!selectedBrands || selectedBrands.length === 0) return [];
    return selectedBrands.map((b) => b?.id || b).filter((id) => id);
  }, [selectedBrands]);

  // Create stable brandIds string to prevent infinite loops - directly from selectedBrands and selectedClients
  const brandIdsKey = useMemo(() => {
    const brandKey = normalizedBrandIds && normalizedBrandIds.length > 0
      ? normalizedBrandIds.sort().join(",")
      : "";
    const clientIdList = selectedClients && selectedClients.length > 0
      ? selectedClients.map((client) => client?.id || client).filter((id) => id)
      : [];
    const clientKey = clientIdList.length > 0 ? clientIdList.sort().join(",") : "";
    return `${brandKey}__${clientKey}`;
  }, [normalizedBrandIds, selectedClients]);

  // Create stable dateRange string to prevent infinite loops
  const dateRangeKey = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return "";
    return `${dateRange[0].format("YYYY-MM-DD")}_${dateRange[1].format(
      "YYYY-MM-DD"
    )}`;
  }, [dateRange]);

  // Cache key
  const cacheKey = useMemo(() => {
    if (!brandIdsKey) return null;
    const dateKey = showCustomRange ? dateRangeKey : dateFilter;
    return `profit-and-loss-${brandIdsKey}-${dateKey}`;
  }, [brandIdsKey, dateRangeKey, dateFilter, showCustomRange]);

  // Check global cache for initial data
  const initialCachedData = useMemo(() => {
    if (!cacheKey) return null;
    return getCachedData(cacheKey);
  }, [cacheKey]);

  const [loading, setLoading] = useState(() => !initialCachedData);
  const [data, setData] = useState(() => initialCachedData || null);

  const fetchingRef = useRef(false);
  const lastFetchedCacheKeyRef = useRef(initialCachedData ? cacheKey : null);

  const handleDateFilterChange = (value) => {
    if (value === "custom") {
      setShowCustomRange(true);
      setDateFilter(null);
    } else {
      setShowCustomRange(false);
      setDateFilter(value);
      setDateRange([]);
    }
  };

  // Calculate date range from filter when fetching
  const getDateRangeFromFilter = (filter) => {
    if (!filter) return [null, null];

    const today = dayjs();
    let start, end;

    switch (filter) {
      case "this_month":
        start = today.startOf("month");
        end = today.endOf("month");
        break;
      case "this_quarter":
        start = today.startOf("quarter");
        end = today.endOf("quarter");
        break;
      case "this_year":
        start = today.startOf("year");
        end = today.endOf("year");
        break;
      case "last_year":
        start = today.subtract(1, "year").startOf("year");
        end = today.subtract(1, "year").endOf("year");
        break;
      default:
        start = today.startOf("month");
        end = today.endOf("month");
    }

    return [start, end];
  };

  const fetchReport = async (force = false) => {
    // Explicitly require brands to be selected - no fallback
    if (!selectedBrands || selectedBrands.length === 0 || !cacheKey) {
      setData(null);
      return;
    }

    // Check GLOBAL cache first if not forcing refresh
    if (!force) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setData(cachedData);
        setLoading(false);
        lastFetchedCacheKeyRef.current = cacheKey;
        return;
      }
    }

    // Already fetched for this key
    if (!force && lastFetchedCacheKeyRef.current === cacheKey) {
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (fetchingRef.current && !force) {
      return;
    }

    // Normalize brand IDs directly from selectedBrands
    const normalizedBrandIdsList = selectedBrands.map((b) => b?.id || b).filter((id) => id);
    if (normalizedBrandIdsList.length === 0) {
      setData(null);
      return;
    }

    fetchingRef.current = true;
    setLoading(true);

    try {
      // Calculate date range based on filter or use custom range
      let start, end;
      if (showCustomRange && dateRange && dateRange.length === 2) {
        start = dateRange[0].format("YYYY-MM-DD");
        end = dateRange[1].format("YYYY-MM-DD");
      } else if (dateFilter) {
        const [calculatedStart, calculatedEnd] =
          getDateRangeFromFilter(dateFilter);
        start = calculatedStart
          ? calculatedStart.format("YYYY-MM-DD")
          : undefined;
        end = calculatedEnd ? calculatedEnd.format("YYYY-MM-DD") : undefined;
      } else {
        // Default to this month if nothing is set
        const [defaultStart, defaultEnd] = getDateRangeFromFilter("this_month");
        start = defaultStart.format("YYYY-MM-DD");
        end = defaultEnd.format("YYYY-MM-DD");
      }

      // Extract client IDs directly from selectedClients
      const clientIdList = selectedClients && selectedClients.length > 0
        ? selectedClients.map((client) => client?.id || client).filter((id) => id)
        : [];

      const params = {
        dateRange: [start, end],
        brandIds: normalizedBrandIdsList,
        clientIds: clientIdList.length > 0 ? clientIdList : undefined,
      };

      const res = await getProfitAndLoss(params);
      setData(res);

      // Cache in GLOBAL cache (5 minute TTL)
      setCachedData(cacheKey, res, CacheTTL.MEDIUM);
      lastFetchedCacheKeyRef.current = cacheKey;
    } catch (error) {
      message.error("Failed to fetch Profit & Loss report");
      setData(null);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Only fetch if brands are explicitly selected
    if (!selectedBrands || selectedBrands.length === 0 || !cacheKey) {
      setData(null);
      return;
    }

    // Check GLOBAL cache first - restore immediately if available
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setData(cachedData);
      setLoading(false);
      lastFetchedCacheKeyRef.current = cacheKey;
      return;
    }

    // Only fetch if not already fetched for this key
    if (lastFetchedCacheKeyRef.current !== cacheKey) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Currency formatter - takes absolute value for display
  // Supports currency conversion when non-USD currency is selected
  const formatCurrency = useCallback((value, useAbsolute = false) => {
    const numValue = parseFloat(value || 0);
    const displayValue = useAbsolute ? Math.abs(numValue) : numValue;
    
    // Apply currency conversion if active
    const convertedValue = isConverted ? (displayValue * exchangeRate) : displayValue;
    
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: displayCurrency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedValue);
    
    // Add swap icon if converted
    if (isConverted) {
      return (
        <Tooltip title={`Original: $${displayValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Rate: 1 USD = ${exchangeRate} ${displayCurrency})`}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {formatted}
            <SwapOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          </span>
        </Tooltip>
      );
    }
    
    return formatted;
  }, [isConverted, exchangeRate, displayCurrency]);
  
  // Legacy alias for backward compatibility
  const currency = formatCurrency;

  // --- Build dynamic columns ---
  const brandColumns =
    data?.brands && normalizedBrandIds.length
      ? data.brands
          .filter((b) => normalizedBrandIds.includes(b.brand_id))
          .map((b) => ({
            title: b.brand_name,
            dataIndex: `brand_${b.brand_id}`,
            key: `brand_${b.brand_id}`,
            align: "right",
          }))
      : [];

  const columns = [
    { title: "Account", dataIndex: "account", key: "account" },
    ...brandColumns,
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      fixed: "right",
    },
  ];

  // --- Build dynamic table rows from account hierarchy ---
  const buildAccountRow = (account, level = 0) => {
    // For P&L, we want to show all values as positive (absolute values)
    // Revenue, COGS, Expenses should all display as positive numbers
    const accountBalance =
      data?.total?.categories?.[account.category]?.find(
        (a) => a.account_id === account.account_id
      )?.balance || 0;

    const row = {
      key: `account_${account.account_id}`,
      account: (
        <span style={{ paddingLeft: `${level * 20}px` }}>
          {account.code ? `${account.code} - ` : ""}
          {account.name}
        </span>
      ),
      total: currency(Math.abs(accountBalance)),
      rawValue: accountBalance, // Store raw value for debugging
      isAccount: true,
      level,
    };

    if (data?.brands && normalizedBrandIds.length) {
      data.brands
        .filter((b) => normalizedBrandIds.includes(b.brand_id))
        .forEach((b) => {
          const brandAccount = b.categories?.[account.category]?.find(
            (a) => a.account_id === account.account_id
          );
          const brandBalance = brandAccount?.balance || 0;
          row[`brand_${b.brand_id}`] = currency(Math.abs(brandBalance));
        });
    }
    return row;
  };

  const buildCategoryTotal = (categoryLabel, categoryKey) => {
    // Category totals should also show as absolute values for clearer P&L presentation
    const totalValue = data?.total?.[`${categoryKey}_total`] || 0;

    const row = {
      key: categoryKey,
      account: <strong>{categoryLabel}</strong>,
      total: currency(Math.abs(totalValue)),
      rawValue: totalValue, // Store raw value for debugging
      isTotal: true,
    };

    if (data?.brands && normalizedBrandIds.length) {
      data.brands
        .filter((b) => normalizedBrandIds.includes(b.brand_id))
        .forEach((b) => {
          const brandTotal = b[`${categoryKey}_total`] || 0;
          row[`brand_${b.brand_id}`] = currency(Math.abs(brandTotal));
        });
    }
    return row;
  };

  const buildCalculatedRow = (label, field) => {
    // Calculated rows (like gross_profit, net_profit) can be positive or negative
    // Don't use absolute value here - show actual profit/loss
    const totalValue = data?.total?.[field] || 0;

    const row = {
      key: field,
      account: <strong>{label}</strong>,
      total: currency(totalValue),
      rawValue: totalValue, // Store raw value for debugging
      isCalculated: true,
    };

    if (data?.brands && normalizedBrandIds.length) {
      data.brands
        .filter((b) => normalizedBrandIds.includes(b.brand_id))
        .forEach((b) => {
          const brandValue = b[field] || 0;
          row[`brand_${b.brand_id}`] = currency(brandValue);
        });
    }
    return row;
  };

  const tableData = data
    ? [
        // Revenue Section
        ...(data.total?.categories?.Revenue || []).map((account) =>
          buildAccountRow(account, 1)
        ),
        buildCategoryTotal("Total Revenue", "revenue"),
        { key: "spacer1", account: "", total: "", isSpac: true },

        // COGS Section
        ...(data.total?.categories?.COGS || []).map((account) =>
          buildAccountRow(account, 1)
        ),
        buildCategoryTotal("Total Cost of Goods Sold", "cogs"),
        { key: "spacer2", account: "", total: "", isSpacer: true },

        buildCalculatedRow("Gross Profit", "gross_profit"),
        { key: "spacer3", account: "", total: "", isSpacer: true },

        // Expense Section
        ...(data.total?.categories?.Expense || []).map((account) =>
          buildAccountRow(account, 1)
        ),
        buildCategoryTotal("Total Operating Expenses", "expense"),
        { key: "spacer4", account: "", total: "", isSpacer: true },

        buildCalculatedRow("Operating Profit", "net_operating_profit"),
        { key: "spacer5", account: "", total: "", isSpacer: true },

        // Other Income Section
        ...(data.total?.categories?.["Other Income"] || []).map((account) =>
          buildAccountRow(account, 1)
        ),
        buildCategoryTotal("Total Other Income", "other_income"),
        { key: "spacer6", account: "", total: "", isSpacer: true },

        // Other Expense Section
        ...(data.total?.categories?.["Other Expense"] || []).map((account) =>
          buildAccountRow(account, 1)
        ),
        buildCategoryTotal("Total Other Expenses", "other_expense"),
      ]
    : [];

  return (
    <Card
      title="Profit and Loss"
      extra={
        <Space>
          <div
            style={{
              padding: "4px 8px",
              background: "#f0f0f0",
              borderRadius: 4,
            }}
          >
            {selectedBrands.length > 0
              ? `${selectedBrands.length} Brand(s) Selected`
              : "No Brands Selected"}
          </div>
          <Select
            placeholder="Date Range"
            style={{ width: 180 }}
            value={showCustomRange ? "custom" : dateFilter}
            onChange={handleDateFilterChange}
          >
            <Select.Option value="this_month">This Month</Select.Option>
            <Select.Option value="this_quarter">This Quarter</Select.Option>
            <Select.Option value="this_year">This Year</Select.Option>
            <Select.Option value="last_year">Last Year</Select.Option>
            <Select.Option value="custom">Custom Range</Select.Option>
          </Select>
          {showCustomRange && (
            <RangePicker
              value={dateRange}
              onChange={(values) => setDateRange(values)}
            />
          )}
          <Button type="primary" onClick={() => fetchReport(true)}>
            Apply
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>
          Period:{" "}
          {(() => {
            let start, end;
            if (showCustomRange && dateRange && dateRange.length === 2) {
              start = dateRange[0];
              end = dateRange[1];
            } else if (dateFilter) {
              const [calculatedStart, calculatedEnd] =
                getDateRangeFromFilter(dateFilter);
              start = calculatedStart;
              end = calculatedEnd;
            } else {
              const [defaultStart, defaultEnd] =
                getDateRangeFromFilter("this_month");
              start = defaultStart;
              end = defaultEnd;
            }
            return `${start.format("DD MMM YYYY")} - ${end.format(
              "DD MMM YYYY"
            )}`;
          })()}
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
        bordered
        rowKey="key"
        loading={loading}
        scroll={{ x: true }}
      />

      {data && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 16px",
            background: "#fafafa",
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 600,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <span>Net Profit / Loss</span>
          <span>{currency(data.total?.net_profit)}</span>
        </div>
      )}
    </Card>
  );
};

export default ProfitAndLoss;
